import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageSquare, TrendingUp, DollarSign, Users, Gauge, Eye } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/hooks/useApi';

interface SMSAnalyticsProps {
  tenantId: string;
  dateRange: {
    start: string;
    end: string;
  };
  userTimezone?: string;
}

interface SMSData {
  totalSMS: number;
  inboundSMS: number;
  outboundSMS: number;
  totalCost: number;
  dailyUsage: Array<{ date: string; inbound: number; outbound: number; total: number; }>;
  locationBreakdown: Array<{ location: string; count: number; inbound: number; outbound: number; cost: number; isOther?: boolean; location_id?: string; service_id?: string; }>;
}

interface SMSDetail {
  ts?: number;
  timestamp?: number;
  datetime?: string;
  direction: string;
  body?: string;
  message?: string;
  event?: string;
  triggered_by?: string;
  by?: string;
  entry_id?: string;
  phone?: string;
  cost?: number;
}

interface PlanLimits {
  limit: number;
  used: number;
  remaining: number;
  usage_percentage: number;
  plan: string;
}



export function SMSAnalytics({ tenantId, dateRange, userTimezone = 'UTC' }: SMSAnalyticsProps) {
  const { isSuperAdmin } = useAuth();
  
  const [smsData, setSmsData] = useState<SMSData>({
    totalSMS: 0,
    inboundSMS: 0,
    outboundSMS: 0,
    totalCost: 0,
    dailyUsage: [],
    locationBreakdown: []
  });
  const [planLimits, setPlanLimits] = useState<PlanLimits | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState<{ location_id: string; service_id: string; location: string } | null>(null);
  const [smsDetails, setSmsDetails] = useState<SMSDetail[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    loadSMSAnalytics();
  }, [tenantId, dateRange]);

  const loadSMSDetails = async (locationId: string, serviceId: string, locationName: string) => {
    if (!tenantId || !locationId || !serviceId) return;
    
    setDetailsLoading(true);
    setSelectedQueue({ location_id: locationId, service_id: serviceId, location: locationName });
    setShowDetailsModal(true);
    
    try {
      // Use new SMS details endpoint
      const data = await apiClient.getSmsDetails(tenantId, {
        date_from: dateRange.start,
        date_to: dateRange.end,
        timezone: userTimezone,
        location_id: locationId,
        service_id: serviceId
      });
      
      console.log('SMS Details API response:', data);
      setSmsDetails(data.messages || []);
    } catch (error) {
      console.error('Failed to load SMS details:', error);
      setSmsDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const loadOtherSMSDetails = async () => {
    if (!tenantId) return;
    
    setDetailsLoading(true);
    setSelectedQueue({ location_id: 'Other', service_id: 'Messages', location: 'Other Messages' });
    setShowDetailsModal(true);
    
    try {
      // Use new SMS details endpoint for "Other" messages
      const data = await apiClient.getSmsDetails(tenantId, {
        date_from: dateRange.start,
        date_to: dateRange.end,
        timezone: userTimezone,
        location_id: 'Other',
        service_id: 'Messages'
      });
      
      console.log('Other Messages Details API response:', data);
      setSmsDetails(data.messages || []);
    } catch (error) {
      console.error('Failed to load other SMS details:', error);
      setSmsDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };
  
  const downloadSMSDetails = () => {
    if (!smsDetails.length || !selectedQueue) return;
    
    const csvHeaders = ['Timestamp', 'Direction', 'Message', 'Event', 'By', 'Entry ID'];
    const csvRows = smsDetails.map(msg => [
      new Date((msg.timestamp || msg.ts || 0) * 1000).toLocaleString(),
      msg.direction,
      `"${(msg.message || msg.body || '').replace(/"/g, '""')}"`, // Escape quotes in CSV
      msg.triggered_by || msg.event || '',
      msg.by || '',
      msg.entry_id || ''
    ]);
    
    const csvContent = [csvHeaders.join(','), ...csvRows.map(row => row.join(','))].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sms-details-${selectedQueue.location.replace(/[^a-zA-Z0-9]/g, '-')}-${dateRange.start}-to-${dateRange.end}.csv`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const loadSMSAnalytics = async () => {
    if (!tenantId) return;
    
    setLoading(true);
    try {
      // Load SMS analytics and plan limits in parallel
      const [smsResponse, limitsResponse] = await Promise.all([
        apiClient.getSmsAnalytics(tenantId, {
          date_from: dateRange.start,
          date_to: dateRange.end,
          timezone: userTimezone
        }),
        apiClient.getPlanLimits(tenantId)
      ]);
      
      if (smsResponse) {
        const inbound = smsResponse.total_inbound || 0;
        const outbound = smsResponse.total_outbound || 0;
        
        const transformedData: SMSData = {
          totalSMS: inbound + outbound,
          inboundSMS: inbound,
          outboundSMS: outbound,
          totalCost: smsResponse.total_cost || 0,
          dailyUsage: (smsResponse.daily_breakdown || []).map((day: any) => ({
            date: day.date,
            inbound: day.inbound || 0,
            outbound: day.outbound || 0,
            total: (day.inbound || 0) + (day.outbound || 0)
          })),
          locationBreakdown: (smsResponse.location_breakdown || []).map((loc: any) => ({
            location: loc.location_id === 'Other' ? 'Other Messages' : `${loc.location_id} / ${loc.service_id}`,
            count: loc.total || ((loc.inbound || 0) + (loc.outbound || 0)),
            inbound: loc.inbound || 0,
            outbound: loc.outbound || 0,
            cost: loc.cost || 0,
            isOther: loc.location_id === 'Other',
            location_id: loc.location_id === 'Other' ? 'Other' : loc.location_id,
            service_id: loc.service_id === 'Messages' ? 'Messages' : loc.service_id
          }))
        };
        setSmsData(transformedData);
      }
      
      if (limitsResponse) {
        setPlanLimits(limitsResponse);
      }
    } catch (error) {
      console.error('Failed to load SMS analytics:', error);
      setSmsData({
        totalSMS: 0,
        inboundSMS: 0,
        outboundSMS: 0,
        totalCost: 0,
        dailyUsage: [],
        locationBreakdown: []
      });
      setPlanLimits(null);
    } finally {
      setLoading(false);
    }
  };

  const pieData = [
    { name: 'Inbound', value: smsData.inboundSMS, color: '#0088FE' },
    { name: 'Outbound', value: smsData.outboundSMS, color: '#00C49F' }
  ];

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SMS Usage Gauge */}
      {planLimits && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gauge className="h-5 w-5" />
              SMS Usage {(() => {
                const now = new Date();
                const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                const rangeStart = dateRange.start.substring(0, 7); // YYYY-MM
                const rangeEnd = dateRange.end.substring(0, 7); // YYYY-MM
                
                if (rangeStart === rangeEnd && rangeStart === currentMonth) {
                  return 'This Month';
                } else if (rangeStart === rangeEnd) {
                  const date = new Date(rangeStart + '-01');
                  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                } else {
                  return 'Selected Period';
                }
              })()}
            </CardTitle>
            <CardDescription>
              {(() => {
                const now = new Date();
                const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                const rangeStart = dateRange.start.substring(0, 7);
                const rangeEnd = dateRange.end.substring(0, 7);
                
                if (rangeStart === rangeEnd && rangeStart === currentMonth) {
                  return `${planLimits.plan} Plan - ${(planLimits.used || 0).toLocaleString()} of ${(planLimits.limit || 0).toLocaleString()} used`;
                } else {
                  return `${planLimits.plan} Plan - ${(smsData.totalSMS || 0).toLocaleString()} SMS in selected period`;
                }
              })()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(() => {
                const now = new Date();
                const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                const rangeStart = dateRange.start.substring(0, 7);
                const rangeEnd = dateRange.end.substring(0, 7);
                
                if (rangeStart === rangeEnd && rangeStart === currentMonth) {
                  // Current month - show plan limits
                  return (
                    <>
                      <Progress value={Math.min((planLimits.usage_percentage || 0), 100)} className="h-3" />
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {(planLimits.remaining || 0).toLocaleString()} remaining
                        </span>
                        <span className={(planLimits.usage_percentage || 0) > 80 ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
                          {(planLimits.usage_percentage || 0).toFixed(1)}% used
                        </span>
                      </div>
                      {(planLimits.usage_percentage || 0) > 90 && (
                        <div className="text-sm text-red-600 font-medium">
                          ⚠️ Approaching SMS limit
                        </div>
                      )}
                    </>
                  );
                } else {
                  // Historical period - show usage without limits
                  const usagePercent = planLimits.limit > 0 ? Math.min((smsData.totalSMS / planLimits.limit) * 100, 100) : 0;
                  return (
                    <>
                      <Progress value={usagePercent} className="h-3" />
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          Historical period
                        </span>
                        <span className="text-muted-foreground">
                          {usagePercent.toFixed(1)}% of monthly limit
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Note: Plan limits apply to current month only
                      </div>
                    </>
                  );
                }
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SMS Overview Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isSuperAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total SMS</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(smsData.totalSMS || 0).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {smsData.inboundSMS} in / {smsData.outboundSMS} out
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inbound SMS</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(smsData.inboundSMS || 0).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Customer messages
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Outbound SMS</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(smsData.outboundSMS || 0).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              System messages
            </p>
          </CardContent>
        </Card>

        {isSuperAdmin && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${(smsData.totalCost || 0).toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">
                $0.0075 per SMS
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Daily SMS Usage</CardTitle>
            <CardDescription>SMS volume by day (filtered by date range)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={smsData.dailyUsage}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [value, name === 'inbound' ? 'Inbound SMS' : 'Outbound SMS']} />
                  <Bar dataKey="inbound" stackId="a" fill="#0088FE" name="Inbound" />
                  <Bar dataKey="outbound" stackId="a" fill="#00C49F" name="Outbound" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>SMS Direction Split</CardTitle>
            <CardDescription>Inbound vs Outbound distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Location/Queue Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>SMS Usage by Queue</CardTitle>
          <CardDescription>SMS volume{isSuperAdmin ? ' and cost' : ''} breakdown by location and service</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {smsData.locationBreakdown.length > 0 ? (
              smsData.locationBreakdown
                .sort((a, b) => {
                  // Put "Other Messages" at the end, otherwise sort by count descending
                  if (a.isOther && !b.isOther) return 1;
                  if (!a.isOther && b.isOther) return -1;
                  return b.count - a.count;
                })
                .map((location, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1">
                    <div className="font-medium text-lg">{location.location}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {location.count} total messages
                      {location.isOther && (
                        <span className="ml-2 text-xs">(not tied to specific queue)</span>
                      )}
                    </div>
                    <div className="flex gap-4 mt-2 text-xs">
                      <span className="text-blue-600">📥 {location.inbound} inbound</span>
                      <span className="text-green-600">📤 {location.outbound} outbound</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <Badge variant="outline" className="text-lg px-3 py-1">
                        {location.count} SMS
                      </Badge>
                      {isSuperAdmin && (
                        <div className="text-xs text-muted-foreground mt-1">
                          ${location.cost.toFixed(2)} cost
                        </div>
                      )}
                    </div>
                    {location.location_id && location.service_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (location.isOther) {
                            loadOtherSMSDetails()
                          } else {
                            loadSMSDetails(location.location_id!, location.service_id!, location.location)
                          }
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Details
                      </Button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No SMS data available for the selected period
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SMS Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>SMS Details - {selectedQueue?.location}</DialogTitle>
            <DialogDescription>
              Detailed SMS history for this queue during the selected period
            </DialogDescription>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {smsDetails.length > 0 ? (
                <div className="space-y-2">
                  {smsDetails.map((message, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${
                      message.direction === 'inbound' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'
                    }`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={message.direction === 'inbound' ? 'default' : 'secondary'}>
                            {message.direction === 'inbound' ? '📥 Inbound' : '📤 Outbound'}
                          </Badge>
                          <span className="text-sm text-gray-500">
                            {message.datetime || new Date((message.timestamp || message.ts || 0) * 1000).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {message.phone && `${message.phone} • `}{message.triggered_by || message.event} {message.by && `by ${message.by}`}
                        </div>
                      </div>
                      <div className="text-sm">
                        {message.message || message.body}
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        {message.entry_id && (
                          <div className="text-xs text-gray-400">
                            Entry: {message.entry_id}
                          </div>
                        )}
                        {isSuperAdmin && message.cost && (
                          <div className="text-xs text-gray-500">
                            Cost: ${message.cost.toFixed(4)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No SMS messages found for this queue in the selected period
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={downloadSMSDetails} disabled={!smsDetails.length}>
              Download CSV
            </Button>
            <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}